import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, MapPin, Clock, Briefcase, Users, Lightbulb, TrendingUp, Target, MessageSquare, CheckCircle, Code, Palette, BarChart, HeadphonesIcon } from 'lucide-react';

export default function DigitalTrentCareers() {
  const [visibleSections, setVisibleSections] = useState(new Set());
  const [expandedJob, setExpandedJob] = useState(null);
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections(prev => new Set([...prev, entry.target.dataset.animate]));
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -100px 0px' }
    );

    const sections = document.querySelectorAll('[data-animate]');
    sections.forEach((section) => observerRef.current.observe(section));

    return () => observerRef.current.disconnect();
  }, []);

  const openPositions = [
    {
      id: 1,
      title: 'Senior Full-Stack Engineer',
      location: 'Remote (US)',
      type: 'Full-time',
      department: 'Engineering',
      description: 'Build scalable web applications and APIs. Work with React, Node.js, and cloud infrastructure to solve complex technical challenges.',
      requirements: ['5+ years full-stack development', 'React & Node.js expertise', 'Cloud platform experience', 'System design knowledge']
    },
    {
      id: 2,
      title: 'Product Designer',
      location: 'New York, NY',
      type: 'Full-time',
      department: 'Design',
      description: 'Shape user experiences from concept to delivery. Create intuitive interfaces that balance user needs with business objectives.',
      requirements: ['3+ years product design', 'Figma proficiency', 'User research experience', 'Portfolio required']
    },
    {
      id: 3,
      title: 'Digital Marketing Manager',
      location: 'Remote',
      type: 'Full-time',
      department: 'Marketing',
      description: 'Drive growth through data-driven marketing strategies. Own campaigns across SEO, content, and paid channels.',
      requirements: ['4+ years digital marketing', 'Analytics expertise', 'Content strategy', 'Campaign management']
    },
    {
      id: 4,
      title: 'Customer Success Lead',
      location: 'San Francisco, CA',
      type: 'Full-time',
      department: 'Customer Success',
      description: 'Build lasting client relationships and ensure project success. Act as the bridge between clients and our delivery teams.',
      requirements: ['3+ years client-facing role', 'Tech industry experience', 'Project management', 'Communication skills']
    },
    {
      id: 5,
      title: 'Frontend Engineer',
      location: 'Remote (Europe)',
      type: 'Full-time',
      department: 'Engineering',
      description: 'Craft pixel-perfect, performant user interfaces. Work with modern frameworks to build engaging web experiences.',
      requirements: ['3+ years frontend development', 'React or Vue expertise', 'CSS mastery', 'Performance optimization']
    },
    {
      id: 6,
      title: 'Business Development Associate',
      location: 'London, UK',
      type: 'Full-time',
      department: 'Sales',
      description: 'Identify and develop new business opportunities. Build relationships with potential clients and drive revenue growth.',
      requirements: ['2+ years BD/Sales', 'Tech sector knowledge', 'CRM proficiency', 'Negotiation skills']
    }
  ];

  return (
    <div className="bg-white text-zinc-900">
      {/* Careers Hero */}
      <section className="border-b border-zinc-200" data-animate="hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-40">
          <div className="grid lg:grid-cols-12 gap-16">
            <div className={`lg:col-span-8 transition-all duration-1000 ease-out ${
              visibleSections.has('hero') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}>
              <div className="inline-block px-4 py-1 bg-zinc-900 text-white text-xs font-medium tracking-wider mb-8">
                CAREERS AT DIGITAL TRENT
              </div>
              <h1 className="text-5xl lg:text-7xl font-bold tracking-tight mb-8 leading-[1.1]">
                Build the future of digital with us
              </h1>
              <p className="text-xl text-zinc-600 mb-10 leading-relaxed max-w-2xl">
                Join a team that\'s redefining what digital solutions can be. We\'re looking for ambitious builders, creative thinkers, and problem solvers ready to make an impact.
              </p>
              <button className="group bg-zinc-900 text-white px-10 py-5 text-base font-medium transition-all duration-300 inline-flex items-center gap-3 hover:px-12">
                View Open Roles
                <ArrowRight size={20} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </div>
            <div className={`lg:col-span-4 flex flex-col justify-center transition-all duration-1000 ease-out delay-200 ${
              visibleSections.has('hero') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}>
              <div className="space-y-8">
                <div className="border-l-4 border-zinc-900 pl-6">
                  <div className="text-5xl font-bold mb-2">35+</div>
                  <div className="text-sm text-zinc-600">Team Members</div>
                </div>
                <div className="border-l-4 border-zinc-900 pl-6">
                  <div className="text-5xl font-bold mb-2">12</div>
                  <div className="text-sm text-zinc-600">Countries</div>
                </div>
                <div className="border-l-4 border-zinc-900 pl-6">
                  <div className="text-5xl font-bold mb-2">6</div>
                  <div className="text-sm text-zinc-600">Open Positions</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Work at Digital Trent */}
      <section className="border-b border-zinc-200" data-animate="why">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
          <div className={`mb-20 transition-all duration-1000 ease-out ${
            visibleSections.has('why') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}>
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">Why Digital Trent</h2>
            <p className="text-xl text-zinc-600 max-w-3xl">We\'re not just offering jobs—we\'re building careers. Here\'s what makes working with us different.</p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {[
              {
                icon: Lightbulb,
                title: 'Ownership & Impact',
                description: 'You own your work from concept to delivery. Your ideas shape products that reach thousands of users. No micromanagement, just trust and accountability.',
                highlight: 'Real autonomy'
              },
              {
                icon: TrendingUp,
                title: 'Accelerated Growth',
                description: 'Learn from senior engineers, designers, and strategists. Work on diverse projects across industries. We invest in your professional development through courses, conferences, and mentorship.',
                highlight: 'Fast-track learning'
              },
              {
                icon: Users,
                title: 'Work That Matters',
                description: 'Build solutions for startups disrupting healthcare, finance, and education. Every project has purpose. You\'ll see the direct impact of your work on real businesses and people.',
                highlight: 'Meaningful projects'
              }
            ].map((item, i) => (
              <div
                key={item.title}
                className={`border-2 border-zinc-900 p-10 transition-all duration-800 ease-out ${
                  visibleSections.has('why') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                }`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <item.icon size={40} className="mb-6" />
                <div className="inline-block px-3 py-1 bg-zinc-900 text-white text-xs font-medium mb-4">
                  {item.highlight}
                </div>
                <h3 className="text-2xl font-bold mb-4">{item.title}</h3>
                <p className="text-zinc-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>

          <div className={`mt-8 grid lg:grid-cols-2 gap-8 transition-all duration-800 ease-out delay-300 ${
            visibleSections.has('why') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}>
            <div className="bg-zinc-100 border border-zinc-200 p-10">
              <Target size={36} className="mb-6" />
              <h3 className="text-2xl font-bold mb-4">Competitive Compensation</h3>
              <p className="text-zinc-600 leading-relaxed mb-6">
                Top-of-market salaries, equity options, and performance bonuses. We believe in rewarding excellent work.
              </p>
              <ul className="space-y-2 text-zinc-600">
                <li className="flex items-start gap-2">
                  <CheckCircle size={20} className="mt-0.5 flex-shrink-0" />
                  <span>Market-leading base salary</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={20} className="mt-0.5 flex-shrink-0" />
                  <span>Equity participation</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={20} className="mt-0.5 flex-shrink-0" />
                  <span>Annual performance bonuses</span>
                </li>
              </ul>
            </div>
            <div className="bg-zinc-900 text-white p-10">
              <MessageSquare size={36} className="mb-6" />
              <h3 className="text-2xl font-bold mb-4">Flexible Culture</h3>
              <p className="text-zinc-300 leading-relaxed mb-6">
                Remote-first, flexible hours, and unlimited PTO. We care about output, not hours logged. Balance work with life.
              </p>
              <ul className="space-y-2 text-zinc-300">
                <li className="flex items-start gap-2">
                  <CheckCircle size={20} className="mt-0.5 flex-shrink-0" />
                  <span>Work from anywhere</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={20} className="mt-0.5 flex-shrink-0" />
                  <span>Flexible schedule</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={20} className="mt-0.5 flex-shrink-0" />
                  <span>Unlimited vacation policy</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Life at Digital Trent */}
      <section className="border-b border-zinc-200 bg-zinc-50" data-animate="life">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-20 items-center">
            <div className={`transition-all duration-1000 ease-out ${
              visibleSections.has('life') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}>
              <h2 className="text-4xl lg:text-5xl font-bold mb-8 leading-tight">
                Life at Digital Trent
              </h2>
              <p className="text-lg text-zinc-600 leading-relaxed mb-8">
                We\'re a distributed team of makers united by curiosity and craft. Our culture is built on transparency, experimentation, and continuous learning.
              </p>
              <div className="space-y-6">
                <div className="border-l-4 border-zinc-900 pl-6">
                  <h4 className="font-bold text-lg mb-2">Collaborative by default</h4>
                  <p className="text-zinc-600">Regular sync sessions, pair programming, design critiques, and open channels. Everyone's voice matters.</p>
                </div>
                <div className="border-l-4 border-zinc-900 pl-6">
                  <h4 className="font-bold text-lg mb-2">Ship early, iterate fast</h4>
                  <p className="text-zinc-600">We embrace rapid prototyping and user feedback. Perfect is the enemy of done. Learn by building.</p>
                </div>
                <div className="border-l-4 border-zinc-900 pl-6">
                  <h4 className="font-bold text-lg mb-2">Celebrate wins, learn from failures</h4>
                  <p className="text-zinc-600">We recognize great work publicly and treat setbacks as learning opportunities. Growth mindset is our default.</p>
                </div>
              </div>
            </div>

            <div className={`space-y-6 transition-all duration-1000 ease-out delay-200 ${
              visibleSections.has('life') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}>
              <div className="bg-white border-2 border-zinc-900 p-8">
                <div className="text-6xl mb-4">"</div>
                <p className="text-lg text-zinc-700 leading-relaxed mb-6 italic">
                  I\'ve learned more in 6 months here than in 2 years at my previous job. The team pushes you to grow, and the projects are genuinely exciting.
                </p>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-zinc-900 text-white flex items-center justify-center font-bold text-lg">
                    SK
                  </div>
                  <div>
                    <div className="font-bold">Sarah Kim</div>
                    <div className="text-sm text-zinc-600">Senior Frontend Engineer</div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div className="bg-zinc-900 text-white p-6">
                  <Users size={32} className="mb-4" />
                  <div className="text-3xl font-bold mb-2">Weekly</div>
                  <div className="text-sm text-zinc-300">Team socials & knowledge shares</div>
                </div>
                <div className="bg-white border border-zinc-200 p-6">
                  <Lightbulb size={32} className="mb-4" />
                  <div className="text-3xl font-bold mb-2">$2K</div>
                  <div className="text-sm text-zinc-600">Annual learning budget</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values in Action */}
      <section className="border-b border-zinc-200" data-animate="values">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
          <div className={`mb-20 transition-all duration-1000 ease-out ${
            visibleSections.has('values') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}>
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">Our Values in Action</h2>
            <p className="text-xl text-zinc-600 max-w-3xl">Not just words on a wall—these are behaviors we live by every day.</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8">
            {[
              {
                value: 'Default to transparency',
                behavior: 'Share context, decisions, and learnings openly',
                example: 'All company metrics visible to everyone. Weekly all-hands where anyone can ask anything. Open salary bands and promotion criteria.'
              },
              {
                value: 'Bias toward action',
                behavior: 'Move fast, experiment, and learn from outcomes',
                example: 'Ship MVPs quickly. Run small experiments before big commitments. Prototype ideas in hours, not weeks.'
              },
              {
                value: 'Sweat the details',
                behavior: 'Quality matters in everything we ship',
                example: 'Code reviews are thorough. Design pixels are intentional. Every client interaction is polished and professional.'
              },
              {
                value: 'Lift each other up',
                behavior: 'Collaborate, mentor, and celebrate as a team',
                example: 'Peer mentorship program. Cross-functional project teams. Public recognition in all-hands meetings.'
              }
            ].map((item, i) => (
              <div
                key={item.value}
                className={`border border-zinc-200 p-10 transition-all duration-800 ease-out ${
                  visibleSections.has('values') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                }`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <h3 className="text-2xl font-bold mb-3">{item.value}</h3>
                <p className="text-zinc-600 font-medium mb-4">{item.behavior}</p>
                <div className="border-l-4 border-zinc-900 pl-4 py-2">
                  <p className="text-sm text-zinc-600 leading-relaxed">{item.example}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="border-b border-zinc-200 bg-zinc-50" data-animate="positions">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
          <div className={`mb-20 transition-all duration-1000 ease-out ${
            visibleSections.has('positions') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}>
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">Open Positions</h2>
            <p className="text-xl text-zinc-600">Find your next opportunity. We\'re hiring across all departments.</p>
          </div>

          <div className="space-y-4">
            {openPositions.map((job, i) => (
              <div
                key={job.id}
                className={`bg-white border-2 border-zinc-200 transition-all duration-800 ease-out hover:border-zinc-900 ${
                  visibleSections.has('positions') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
                } ${expandedJob === job.id ? 'border-zinc-900' : ''}`}
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                <div
                  className="p-8 cursor-pointer"
                  onClick={() => setExpandedJob(expandedJob === job.id ? null : job.id)}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold mb-3">{job.title}</h3>
                      <div className="flex flex-wrap gap-4 text-sm text-zinc-600">
                        <div className="flex items-center gap-2">
                          <MapPin size={16} />
                          <span>{job.location}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock size={16} />
                          <span>{job.type}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Briefcase size={16} />
                          <span>{job.department}</span>
                        </div>
                      </div>
                    </div>
                    <button className="group bg-zinc-900 text-white px-6 py-3 text-sm font-medium transition-all duration-300 inline-flex items-center gap-2 hover:px-8 self-start lg:self-center">
                      Apply Now
                      <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </button>
                  </div>
                </div>

                {expandedJob === job.id && (
                  <div className="border-t-2 border-zinc-200 p-8 bg-zinc-50">
                    <div className="grid lg:grid-cols-2 gap-8">
                      <div>
                        <h4 className="font-bold mb-3 text-lg">About the role</h4>
                        <p className="text-zinc-600 leading-relaxed">{job.description}</p>
                      </div>
                      <div>
                        <h4 className="font-bold mb-3 text-lg">What we\'re looking for</h4>
                        <ul className="space-y-2">
                          {job.requirements.map((req, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-zinc-600">
                              <CheckCircle size={18} className="mt-0.5 flex-shrink-0" />
                              <span>{req}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className={`mt-12 text-center transition-all duration-800 ease-out delay-500 ${
            visibleSections.has('positions') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}>
            <p className="text-zinc-600 mb-4">Don\'t see a perfect fit?</p>
            <button className="border-2 border-zinc-900 text-zinc-900 px-8 py-4 text-base font-medium transition-all duration-300 hover:bg-zinc-900 hover:text-white">
              Send Us Your Resume
            </button>
          </div>
        </div>
      </section>

      {/* Hiring Process */}
      <section className="border-b border-zinc-200" data-animate="process">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
          <div className={`text-center mb-20 transition-all duration-1000 ease-out ${
            visibleSections.has('process') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}>
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">Our Hiring Process</h2>
            <p className="text-xl text-zinc-600 max-w-2xl mx-auto">Transparent, efficient, and designed to get to know you beyond the resume.</p>
          </div>

          <div className="space-y-12">
            {[
              {
                step: '01',
                title: 'Application Review',
                duration: '1-2 days',
                description: 'We review your application, portfolio, and experience. If there\'s a match, we\'ll reach out to schedule a call.'
              },
              {
                step: '02',
                title: 'Initial Conversation',
                duration: '30 minutes',
                description: 'Casual video call with our hiring manager. We discuss your background, motivations, and answer questions about the role.'
              },
              {
                step: '03',
                title: 'Technical/Skills Assessment',
                duration: '1-3 hours',
                description: 'Role-specific evaluation. Engineers solve problems, designers complete challenges, marketers present strategies. Flexible timing.'
              },
              {
                step: '04',
                title: 'Team Interviews',
                duration: '2-3 hours',
                description: 'Meet potential teammates across functions. Deep-dive conversations about how you work, collaborate, and solve problems.'
              },
              {
                step: '05',
                title: 'Final Decision',
                duration: '2-3 days',
                description: 'We make our decision and extend an offer. You\'ll have time to review, ask questions, and decide if we\'re the right fit.'
              }
            ].map((item, i) => (
              <div
                key={item.step}
                className={`flex gap-8 transition-all duration-800 ease-out ${
                  visibleSections.has('process') ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
                }`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="flex-shrink-0 w-16 h-16 bg-zinc-900 text-white flex items-center justify-center text-xl font-bold">
                  {item.step}
                </div>
                <div className={`flex-1 border-l-4 border-zinc-900 pl-8 ${i < 4 ? 'pb-4' : ''}`}>
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-3">
                    <h3 className="text-2xl font-bold">{item.title}</h3>
                    <div className="text-sm text-zinc-600 bg-zinc-100 px-3 py-1 inline-block mt-2 lg:mt-0">
                      {item.duration}
                    </div>
                  </div>
                  <p className="text-zinc-600 leading-relaxed text-lg">{item.description}</p>
                </div>
              </div>
            ))}
          </div>

          <div className={`mt-16 bg-zinc-100 border border-zinc-200 p-10 transition-all duration-800 ease-out delay-500 ${
            visibleSections.has('process') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}>
            <h3 className="text-xl font-bold mb-3">Average time to hire: 2-3 weeks</h3>
            <p className="text-zinc-600 leading-relaxed">
              We move quickly but thoughtfully. You\'ll receive updates at every stage, and we\'re always available to answer questions throughout the process.
            </p>
          </div>
        </div>
      </section>

      {/* Who Thrives Here */}
      <section className="border-b border-zinc-200 bg-zinc-900 text-white" data-animate="thrives">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
          <div className={`mb-20 transition-all duration-1000 ease-out ${
            visibleSections.has('thrives') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
          }`}>
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">Who Thrives Here</h2>
            <p className="text-xl text-zinc-400 max-w-3xl">You don\'t need to check every box—but if these resonate, you\'ll probably love it here.</p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            <div className={`space-y-6 transition-all duration-800 ease-out ${
              visibleSections.has('thrives') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}>
              <div className="border-l-4 border-white pl-6">
                <h3 className="text-xl font-bold mb-2">Self-directed builders</h3>
                <p className="text-zinc-400">You don\'t need hand-holding. Give you a problem and clear context, and you\'ll find a path forward.</p>
              </div>
              <div className="border-l-4 border-white pl-6">
                <h3 className="text-xl font-bold mb-2">Curious learners</h3>
                <p className="text-zinc-400">You ask "why" and "how might we." You\'re energized by learning new tools, frameworks, and approaches.</p>
              </div>
              <div className="border-l-4 border-white pl-6">
                <h3 className="text-xl font-bold mb-2">Clear communicators</h3>
                <p className="text-zinc-400">You write concisely, present ideas clearly, and aren\'t afraid to ask questions or push back constructively.</p>
              </div>
              <div className="border-l-4 border-white pl-6">
                <h3 className="text-xl font-bold mb-2">Quality-focused</h3>
                <p className="text-zinc-400">You care about craft. Whether it\'s code, design, or copy—you sweat the details because it matters.</p>
              </div>
            </div>

            <div className={`space-y-6 transition-all duration-800 ease-out delay-200 ${
              visibleSections.has('thrives') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
            }`}>
              <div className="border-l-4 border-white pl-6">
                <h3 className="text-xl font-bold mb-2">Impact-driven</h3>
                <p className="text-zinc-400">You want your work to matter. You\'re motivated by seeing your contributions create real value for users and businesses.</p>
              </div>
              <div className="border-l-4 border-white pl-6">
                <h3 className="text-xl font-bold mb-2">Collaborative teammates</h3>
                <p className="text-zinc-400">You believe great work comes from diverse perspectives. You\'re generous with feedback and open to receiving it.</p>
              </div>
              <div className="border-l-4 border-white pl-6">
                <h3 className="text-xl font-bold mb-2">Adaptable problem-solvers</h3>
                <p className="text-zinc-400">You thrive in ambiguity. When requirements shift or challenges emerge, you adjust and find solutions.</p>
              </div>
              <div className="border-l-4 border-white pl-6">
                <h3 className="text-xl font-bold mb-2">Long-term thinkers</h3>
                <p className="text-zinc-400">You\'re not just chasing the next role. You want to grow with a company, build expertise, and make lasting contributions.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Careers CTA */}
      <section data-animate="cta">
        <div className={`max-w-5xl mx-auto px-6 lg:px-8 py-24 lg:py-40 text-center transition-all duration-1000 ease-out ${
          visibleSections.has('cta') ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
        }`}>
          <h2 className="text-4xl lg:text-6xl font-bold mb-8 leading-tight">
            Ready to build something great?
          </h2>
          <p className="text-xl text-zinc-600 mb-12 max-w-2xl mx-auto leading-relaxed">
            We\'re always looking for talented people who want to do the best work of their careers. Even if you don\'t see an open role, we\'d love to hear from you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="group bg-zinc-900 text-white px-10 py-5 text-base font-medium transition-all duration-300 inline-flex items-center justify-center gap-3 hover:px-12">
              Explore Open Roles
              <ArrowRight size={20} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button className="border-2 border-zinc-900 text-zinc-900 px-10 py-5 text-base font-medium transition-all duration-300 hover:bg-zinc-900 hover:text-white">
              Get in Touch
            </button>
          </div>
          <p className="text-sm text-zinc-500 mt-8">
            Questions? Email us at <span className="font-medium text-zinc-900">careers@digitaltrent.com</span>
          </p>
        </div>
      </section>
    </div>
  );
}